import React, { useEffect, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import { useCoClient } from "../../data/store";
import { createBankAccount, setPettyCash, type NewBankAccount } from "../../data/banksApi";
import {
  dbTrue,
  fetchZohoRecords,
  type ZohoBankAccountDTO,
  type ZohoCurrencyDTO,
} from "../../data/zohoRecordsApi";
import {
  BANK_GROUP_LABEL,
  canBePettyCash,
  formatBalance,
  groupBankAccounts,
  isPettyCash,
  type BankGroup,
} from "../../domain/banks";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { SubStepShell } from "../../components/SubStepShell";
import { VideoPlaceholder } from "../../components/VideoPlaceholder";
import { cn } from "../../components/cn";
import { useSubStepNav } from "../useSubStepNav";

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
      {children}
    </span>
  );
}

/**
 * The account on the left, what it holds on the right.
 *
 * No account number beside the name: Zoho's own naming convention already puts
 * it there ("HDFC - 50200098765432"), so a mask next to it repeats the same
 * digits twice. On the rare account named without one there is nothing to
 * repeat, and a bare masked tail on its own would say less than the name does.
 */
function AccountRow({
  account,
  fresh,
  marking,
  onTogglePetty,
}: {
  account: ZohoBankAccountDTO;
  fresh: boolean;
  /** A petty-cash mark is being saved somewhere on the list. */
  marking: boolean;
  onTogglePetty: (account: ZohoBankAccountDTO, next: boolean) => void;
}) {
  const uncategorised = Number(account.uncategorised_count) || 0;
  const petty = isPettyCash(account);

  return (
    <li
      className={cn(
        "flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border p-3",
        // The one we just made, so it can be found in a list of fourteen.
        fresh && "border-primary/40 bg-primary/5"
      )}
    >
      <span className="flex flex-1 flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{account.account_name ?? account.account_id}</span>
        {petty && <Chip>Cash account</Chip>}
        {dbTrue(account.is_primary) && <Chip>Primary</Chip>}
        {!dbTrue(account.is_active) && <Chip>Inactive in Zoho</Chip>}
        {fresh && <Chip>Just added</Chip>}
        {/*
          The only number on this screen that is a job rather than a fact:
          transactions Zoho has fetched and nobody has categorised. They are
          what makes an opening balance disagree with the bank.
        */}
        {uncategorised > 0 && (
          <span className="shrink-0 rounded-sm bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400">
            {uncategorised} uncategorised
          </span>
        )}
      </span>

      {/*
        ZOUR-120: for the client who already keeps petty cash in Zoho as a bank
        account with no number. Bank rows only — a Zoho cash account takes no
        statements, so it cannot carry a petty cash register. Writes our copy
        only; Zoho has no such flag.
      */}
      {/* A marked row always keeps its way out, even if Zoho has a number for it now. */}
      {(canBePettyCash(account) || petty) && (
        <button
          type="button"
          onClick={() => onTogglePetty(account, !petty)}
          disabled={marking}
          className="shrink-0 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
        >
          {petty ? "Not a cash account" : "Mark as cash account"}
        </button>
      )}

      <span className="font-mono text-sm text-muted-foreground">
        {formatBalance(account.balance, account.currency_code)}
      </span>
    </li>
  );
}

function GroupSection({
  group,
  accounts,
  freshId,
  marking,
  onTogglePetty,
}: {
  group: BankGroup;
  accounts: ZohoBankAccountDTO[];
  freshId: string | null;
  marking: boolean;
  onTogglePetty: (account: ZohoBankAccountDTO, next: boolean) => void;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-muted-foreground">{BANK_GROUP_LABEL[group]}</h3>
      <ul className="flex flex-col gap-2">
        {accounts.map((account) => (
          <AccountRow
            key={account.account_id}
            account={account}
            fresh={account.account_id === freshId}
            marking={marking}
            onTogglePetty={onTogglePetty}
          />
        ))}
      </ul>
    </section>
  );
}

const INPUT_CLASS = "h-9 rounded-md border bg-background px-2 text-sm";

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">
        {label}
        {hint && <span className="font-normal text-muted-foreground/70"> · {hint}</span>}
      </span>
      {children}
    </label>
  );
}

const EMPTY_FORM: NewBankAccount = {
  account_name: "",
  account_type: "bank",
  bank_name: "",
  account_number: "",
  ifsc: "",
  // "" means "the base currency" until the CA picks otherwise — the select
  // below resolves it against the enabled list rather than guessing here.
  currency_id: "",
  is_petty_cash: false,
};

/**
 * Add an account to the client's real Zoho Books.
 *
 * THIS WRITES. It is the only control on the banks step that does, and there is
 * no undo — an account added by mistake has to be deleted in Zoho, and one with
 * transactions posted to it cannot be deleted at all.
 *
 * No confirmation dialog in front of it, on purpose. The form is the
 * confirmation: everything that will be sent is on screen and editable up to
 * the moment the button is pressed, and the button names where it is going. A
 * second screen saying "are you sure" would add no fact the first one lacks,
 * and a confirm that never says anything new is one people learn to click past.
 *
 * No opening balance field. Zoho keeps opening balances on their own endpoint,
 * as one dated set for the whole organisation, so a balance could not travel
 * with the account even if this asked for one — and they have their own step.
 */
function AddBankAccount({
  clientId,
  currencies,
  onAdded,
}: {
  clientId: ClientId;
  currencies: ZohoCurrencyDTO[];
  onAdded: (accounts: ZohoBankAccountDTO[], accountId: string, rereadError: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewBankAccount>(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  function set(patch: Partial<NewBankAccount>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  const name = form.account_name.trim();
  const petty = form.account_type === "bank" && form.is_petty_cash === true;

  // The base currency leads and is the default: that is what Zoho would pick
  // if we said nothing, so the select starts on the truth rather than a blank.
  // Everything else follows alphabetically by code, the way a CA scans for
  // "USD". The choice is worth surfacing at all because it is set-once — Zoho
  // fixes an account's currency after transactions land on it.
  const base = currencies.find((currency) => dbTrue(currency.is_base_currency));
  const others = currencies
    .filter((currency) => !dbTrue(currency.is_base_currency))
    .sort((a, b) => (a.currency_code ?? "").localeCompare(b.currency_code ?? ""));
  const chosenCurrency = form.currency_id === "" ? base?.currency_id ?? "" : form.currency_id;

  async function submit() {
    if (name === "" || sending) return;

    setSending(true);
    setError(null);

    try {
      const result = await createBankAccount(clientId, {
        ...form,
        account_name: name,
        currency_id: chosenCurrency,
        // A cash box has no number, bank or IFSC; anything typed before the
        // tick went on is dropped rather than sent (the backend drops it too).
        ...(petty ? { bank_name: "", account_number: "", ifsc: "" } : {}),
        is_petty_cash: petty,
      });
      if (!liveRef.current) return;

      setForm(EMPTY_FORM);
      setOpen(false);
      onAdded(result.bank_accounts, result.created.account_id, result.reread_error);
    } catch (caught: unknown) {
      if (!liveRef.current) return;
      // Zoho's own refusal comes through here — "The account name already
      // exists" and the like — and is worth more on screen than anything we
      // would write over it.
      setError(caught instanceof Error ? caught.message : "Could not add the account.");
    } finally {
      if (liveRef.current) setSending(false);
    }
  }

  if (!open) {
    return (
      <div>
        <CoButton onClick={() => setOpen(true)}>
          Add bank account
        </CoButton>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Account name">
          <input
            value={form.account_name}
            onChange={(event) => set({ account_name: event.target.value })}
            placeholder={petty ? "Cash – Chennai office" : "HDFC Current - 5432"}
            className={INPUT_CLASS}
            autoFocus
          />
        </Field>

        <Field label="Type">
          <select
            value={form.account_type}
            onChange={(event) => {
              const accountType = event.target.value as NewBankAccount["account_type"];
              // A credit card cannot be petty cash, so the tick goes with the type.
              set(accountType === "bank" ? { account_type: accountType } : { account_type: accountType, is_petty_cash: false });
            }}
            className={INPUT_CLASS}
          >
            <option value="bank">Bank account</option>
            <option value="credit_card">Credit card</option>
          </select>
        </Field>

        {/*
          ZOUR-120: petty cash is a Zoho BANK account with no number — Zoho's
          own cash accounts refuse statement imports, and the tenant reconciles
          the petty cash register like a bank statement.
        */}
        {form.account_type === "bank" && (
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={petty}
              onChange={(event) => set({ is_petty_cash: event.target.checked })}
            />
            This is a cash account (no account number needed)
          </label>
        )}

        {petty ? (
          <p className="text-xs text-muted-foreground sm:col-span-2">
            Say where the cash is kept, e.g. &lsquo;Cash – Chennai office&rsquo;. &lsquo;Petty Cash&rsquo; on its own
            is already taken in your books.
          </p>
        ) : (
          <>
            <Field label="Bank" hint="optional">
              <input
                value={form.bank_name ?? ""}
                onChange={(event) => set({ bank_name: event.target.value })}
                placeholder="HDFC Bank"
                className={INPUT_CLASS}
              />
            </Field>

            <Field label="Account number" hint="optional">
              <input
                value={form.account_number ?? ""}
                onChange={(event) => set({ account_number: event.target.value })}
                className={cn(INPUT_CLASS, "font-mono")}
              />
            </Field>

            <Field label="IFSC" hint="optional">
              <input
                value={form.ifsc ?? ""}
                onChange={(event) => set({ ifsc: event.target.value.toUpperCase() })}
                className={cn(INPUT_CLASS, "font-mono")}
              />
            </Field>
          </>
        )}

        {/*
          Only the currencies the organisation has ENABLED, because those are
          the only ones Zoho will take. A client with just the base currency
          sees a one-option select that is already right; the hint answers the
          CA whose currency is missing. Hidden entirely when we hold no
          currencies to offer — Zoho then defaults to base on its own.
        */}
        {currencies.length > 0 && (
          <Field label="Currency" hint="enable more on the Currency step">
            <select
              value={chosenCurrency}
              onChange={(event) => set({ currency_id: event.target.value })}
              className={INPUT_CLASS}
            >
              {[...(base ? [base] : []), ...others].map((currency) => (
                <option key={currency.currency_id} value={currency.currency_id}>
                  {currency.currency_code ?? currency.currency_id}
                  {currency.currency_name ? ` — ${currency.currency_name}` : ""}
                  {dbTrue(currency.is_base_currency) ? " (base)" : ""}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>

      {error !== null && <Callout variant="warning">{error}</Callout>}

      <div className="flex items-center gap-2">
        <CoButton onClick={() => void submit()} disabled={name === "" || sending}>
          {sending ? "Adding…" : "Add to Zoho Books"}
        </CoButton>
        <CoButton
          variant="outline"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
          disabled={sending}
        >
          Cancel
        </CoButton>
      </div>
    </div>
  );
}

export function BanksReviewStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "banks-review");

  const [accounts, setAccounts] = useState<ZohoBankAccountDTO[] | null>(null);
  const [currencies, setCurrencies] = useState<ZohoCurrencyDTO[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [freshId, setFreshId] = useState<string | null>(null);
  const [rereadError, setRereadError] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoadError(null);

    // Currencies ride along for the add form's dropdown — the account's
    // currency is set-once in Zoho, so it is asked at create time.
    fetchZohoRecords(clientId, ["bank_accounts", "currencies"])
      .then((records) => {
        if (!live) return;
        setAccounts(records.bank_accounts ?? []);
        setCurrencies(records.currencies ?? []);
      })
      .catch((error: unknown) => {
        if (live) setLoadError(error instanceof Error ? error.message : "Could not load the bank accounts.");
      });

    return () => {
      live = false;
    };
  }, [clientId]);

  if (!client) return null;

  const clientName = client.name || "this client";
  const sections = groupBankAccounts(accounts ?? []);
  const pending = (accounts ?? []).reduce(
    (total, account) => total + (Number(account.uncategorised_count) || 0),
    0
  );

  // The create hands back the whole list re-read from Zoho, so there is nothing
  // else to fetch — the account arrives with the currency and ids Zoho filled
  // in rather than the fields we sent.
  function accountAdded(next: ZohoBankAccountDTO[], accountId: string, reread: string | null) {
    setAccounts(next);
    setFreshId(accountId === "" ? null : accountId);
    setRereadError(reread);
  }

  // Our database only, never Zoho. The list comes back whole, so the row moves
  // between Bank accounts and Cash without a second fetch.
  async function togglePetty(account: ZohoBankAccountDTO, next: boolean) {
    if (marking) return;
    setMarking(true);
    setMarkError(null);
    try {
      setAccounts(await setPettyCash(clientId, account.account_id, next));
    } catch (caught: unknown) {
      setMarkError(caught instanceof Error ? caught.message : "Could not change the cash account mark.");
    } finally {
      setMarking(false);
    }
  }

  // Continue is the review sign-off, so the accounts have to actually be on
  // screen first. Zero accounts stays passable — the warning above the add
  // form covers that case without stranding a client who genuinely has none.
  const reviewable = loadError === null && accounts !== null;

  return (
    <SubStepShell
      title="Banks & Credit Cards"
      description={`Below are the bank and credit card accounts currently available in ${clientName}'s Zoho Books account.`}
      thenWhat={reviewable ? "Continue → Currency Settings." : "The accounts have to load before this step can be signed off."}
      onContinue={() => void goNext()}
      continueDisabled={!reviewable}
    >
      {loadError !== null ? (
        <Callout variant="warning">{loadError}</Callout>
      ) : accounts === null ? (
        <p className="text-sm text-muted-foreground">Reading the bank accounts…</p>
      ) : (
        <>
          {accounts.length === 0 ? (
            <Callout variant="warning">
              No banking accounts were read from {clientName}'s books. Add one below, or add it in Zoho Books
              and re-run the read on Confirm Connection to Zoho.
            </Callout>
          ) : (
            sections.map((section) => (
              <GroupSection
                key={section.group}
                group={section.group}
                accounts={section.accounts}
                freshId={freshId}
                marking={marking}
                onTogglePetty={(account, next) => void togglePetty(account, next)}
              />
            ))
          )}

          {markError !== null && <Callout variant="warning">{markError}</Callout>}

          {rereadError !== null && <Callout variant="warning">{rereadError}</Callout>}

          {pending > 0 && (
            <Callout variant="warning">
              {pending} fetched {pending === 1 ? "transaction is" : "transactions are"} still uncategorised in
              Zoho Books. Clear them before opening balances, or the balances will not agree with the bank.
            </Callout>
          )}

          <p className="text-sm text-muted-foreground">
            Need to add another bank account? Watch the video below and add the bank account to Zoho Books.
          </p>

          <AddBankAccount clientId={clientId} currencies={currencies} onAdded={accountAdded} />

          <div className="flex flex-col gap-3 rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">
              Add bank credentials in Zoho to reduce manual work - the bank feed keeps transactions current
              without a statement upload each month.
            </p>
            {/* Official Zoho video: "How to connect your bank account". */}
            <VideoPlaceholder caption="How to add bank credentials in Zoho" videoId="P2LKIDcXGa0" />
          </div>

          {/*
            NO CLOSING NOTE about opening balances. There was one, saying the
            figures here are today's and that opening balances have their own
            step. Both true, neither asked: nothing on this screen offers to set
            a balance, so the note answered a question it had itself raised.

            The reason it cannot be asked here still holds, and is worth keeping
            written down. Zoho stores opening balances as one dated set for the
            whole organisation, on `/settings/openingbalances` — not as a figure
            hanging off an account — so the add form above could not take one
            even if somebody wanted it to. See `BankAccountCreator`.
          */}
        </>
      )}
    </SubStepShell>
  );
}
